import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, switchMap, map, first } from 'rxjs/operators';
import { TicketStatus, Ticket, PayoutPolicy } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class DashboardTicketService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  public getMoreTickets = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
    status: string[],
    ticketingPlatform: string[],
    categories: string[],
    payoutStatus: string[],
    purchaseFlow: string[],
    dateRangeStart: string,
    dateRangeEnd: string
  }>();

  public getMorePendingTickets = new Subject<{
    limit: number,
    offset: number,
    status: string[],
    dateRangeStart: string,
    dateRangeEnd: string
  }>();

  public getMorePayouts = new Subject<{
    limit: number,
    offset: number,
    searchQuery: string,
    ticketingPlatform: string[],
    payoutStatus: string[],
    payoutMethod: string[],
    expiredEventsOnly: boolean;
    updatedMoreThan21hAgo: boolean;
    payoutPolicy: PayoutPolicy[],
    dateRangeStart: string,
    dateRangeEnd: string
  }>();

  exportFilteredTickets(
    filters: Partial<{
      eventId,
      limit,
      offset,
      searchQuery,
      categories,
      status,
      payoutStatus,
      purchaseFlow,
      ticketingPlatform,
      dateRangeStart,
      dateRangeEnd,
    }>,
  ): Observable<ArrayBuffer> {
    const route = filters.eventId ? `${this.configUrl}exploreTicketsByEvent/${filters.eventId.toString()}` : `${this.configUrl}exploreTickets`;
    const params = new HttpParams({
      fromObject: {
        format: 'csv',
        limit: filters.limit ? filters.limit.toString() : '',
        offset: filters.offset ? filters.offset.toString() : '',
        searchQuery: filters.searchQuery ? encodeURIComponent(filters.searchQuery.toString()) : '',
        ...filters.categories ? filters.categories?.filter( (c) => !!c)?.reduce( (acc, c, i) => ({[`categories[${i}]`]: c, ...acc}), {}) : '',
        ...filters.payoutStatus ? filters.payoutStatus.filter( (ps) => !!ps)?.reduce( (acc, ps, i) => ({[`payoutStatus[${i}]`]: ps, ...acc}), {}) : '',
        ...filters.purchaseFlow ? filters.purchaseFlow.filter( (pf) => !!pf)?.reduce( (acc, pf, i) => ({[`purchaseFlow[${i}]`]: pf, ...acc}), {}) : '',
        ...filters.status ? filters.status.filter( (s) => !!s)?.reduce( (acc, s, i) => ({[`status[${i}]`]: s, ...acc}), {}) : '',
        ...filters.ticketingPlatform ? filters.ticketingPlatform.filter( (tp) => !!tp)?.reduce( (acc, tp, i) => ({[`ticketingPlatform[${i}]`]: tp, ...acc}), {}) : '',
        dateRangeStart: filters.dateRangeStart ? filters.dateRangeStart.toString() : '',
        dateRangeEnd: filters.dateRangeEnd ? filters.dateRangeEnd.toString() : '',
      },
    });
    return this.http.get(route, {params, responseType: 'arraybuffer'}).pipe(
      map((file: ArrayBuffer) => file),
    );
  }

  getTicketList(eventId?: number) {
    const route = eventId ? `${this.configUrl}exploreTicketsByEvent/${eventId.toString()}` : `${this.configUrl}exploreTickets`;
    return this.getMoreTickets.pipe(
      // wait 600ms after each keystroke before considering the term
      debounceTime(600),
      // switch to new search observable each time the term changes
      switchMap( ({
        limit,
        offset,
        searchQuery,
        status,
        ticketingPlatform,
        categories,
        payoutStatus,
        purchaseFlow,
        dateRangeStart,
        dateRangeEnd,
      }) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery.toString()),
            ...categories?.filter( (c) => !!c)?.reduce( (acc, c, i) => ({[`categories[${i}]`]: c, ...acc}), {}),
            ...payoutStatus?.filter( (ps) => !!ps)?.reduce( (acc, ps, i) => ({[`payoutStatus[${i}]`]: ps, ...acc}), {}),
            ...purchaseFlow?.filter( (pf) => !!pf)?.reduce( (acc, pf, i) => ({[`purchaseFlow[${i}]`]: pf, ...acc}), {}),
            ...status?.filter( (s) => !!s)?.reduce( (acc, s, i) => ({[`status[${i}]`]: s, ...acc}), {}),
            ...ticketingPlatform?.filter( (tp) => !!tp)?.reduce( (acc, tp, i) => ({[`ticketingPlatform[${i}]`]: tp, ...acc}), {}),
            dateRangeStart: dateRangeStart.toString(),
            dateRangeEnd: dateRangeEnd.toString(),
          },
        });
        return this.http.get<{rows: Ticket[], count: number}>(route, {params});
      }),
    );
  }

  getPendingTicketList() {
    const route = `${this.configUrl}exploreTickets`;
    return this.getMorePendingTickets.pipe(
      // switch to new search observable each time the term changes
      switchMap( ({
        limit,
        offset,
        status,
        dateRangeStart,
        dateRangeEnd,
      }) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            ...status?.filter( (s) => !!s)?.reduce( (acc, s, i) => ({[`status[${i}]`]: s, ...acc}), {}),
            dateRangeStart: dateRangeStart.toString(),
            dateRangeEnd: dateRangeEnd.toString(),
          },
        });
        return this.http.get<{rows: Ticket[], count: number}>(route, {params});
      }),
    );
  }

  exportFilteredPayouts(
    filters: Partial<{
      limit,
      offset,
      searchQuery,
      payoutStatus,
      payoutMethod,
      expiredEventsOnly,
      updatedMoreThan21hAgo,
      payoutPolicy,
      ticketingPlatform,
      dateRangeStart,
      dateRangeEnd,
    }>,
  ): Observable<ArrayBuffer> {
    const route = `${this.configUrl}explorePayouts`;
    const params = new HttpParams({
      fromObject: {
        format: 'csv',
        limit: filters.limit ? filters.limit.toString() : '',
        offset: filters.offset ? filters.offset.toString() : '',
        searchQuery: filters.searchQuery ? encodeURIComponent(filters.searchQuery.toString()) : '',
        ...filters.payoutPolicy ? filters.payoutPolicy.filter( (pp) => !!pp)?.reduce( (acc, pp, i) => ({[`payoutPolicy[${i}]`]: pp, ...acc}), {}) : '',
        ...filters.payoutStatus ? filters.payoutStatus.filter( (ps) => !!ps)?.reduce( (acc, ps, i) => ({[`payoutStatus[${i}]`]: ps, ...acc}), {}) : '',
        ...filters.payoutMethod ? filters.payoutMethod.filter( (pm) => !!pm)?.reduce( (acc, pm, i) => ({[`payoutMethod[${i}]`]: pm, ...acc}), {}) : '',
        expiredEventsOnly: filters.expiredEventsOnly,
        updatedMoreThan21hAgo: filters.updatedMoreThan21hAgo,
        ...filters.ticketingPlatform ? filters.ticketingPlatform.filter( (tp) => !!tp)?.reduce( (acc, tp, i) => ({[`ticketingPlatform[${i}]`]: tp, ...acc}), {}) : '',
        dateRangeStart: filters.dateRangeStart ? filters.dateRangeStart.toString() : '',
        dateRangeEnd: filters.dateRangeEnd ? filters.dateRangeEnd.toString() : '',
      },
    });
    return this.http.get(route, {params, responseType: 'arraybuffer'}).pipe(
      map((file: ArrayBuffer) => file),
    );
  }

  getPayoutList() {
    const route = `${this.configUrl}explorePayouts`;
    return this.getMorePayouts.pipe(
      // wait 600ms after each keystroke before considering the term
      debounceTime(600),
      // switch to new search observable each time the term changes
      switchMap( ({
        limit,
        offset,
        searchQuery,
        ticketingPlatform,
        payoutStatus,
        payoutMethod,
        expiredEventsOnly,
        updatedMoreThan21hAgo,
        payoutPolicy,
        dateRangeStart,
        dateRangeEnd,
      }) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery.toString()),
            ...payoutPolicy?.filter( (pp) => !!pp)?.reduce( (acc, pp, i) => ({[`payoutPolicy[${i}]`]: pp, ...acc}), {}),
            ...payoutStatus?.filter( (ps) => !!ps)?.reduce( (acc, ps, i) => ({[`payoutStatus[${i}]`]: ps, ...acc}), {}),
            ...payoutMethod?.filter( (pm) => !!pm)?.reduce( (acc, pm, i) => ({[`payoutMethod[${i}]`]: pm, ...acc}), {}),
            expiredEventsOnly,
            updatedMoreThan21hAgo,
            ...ticketingPlatform?.filter( (tp) => !!tp)?.reduce( (acc, tp, i) => ({[`ticketingPlatform[${i}]`]: tp, ...acc}), {}),
            dateRangeStart: dateRangeStart.toString(),
            dateRangeEnd: dateRangeEnd.toString(),
          },
        });
        return this.http.get<{rows: Ticket[], count: number}>(route, {params});
      }),
    );
  }

  getTicketsStats(eventId: number): Observable<Array<{status: TicketStatus, statusNb: number}>> {
    const route = `${this.configUrl}events/${eventId}/stats`;
    return this.http.get<Array<{status: TicketStatus, statusNb: number}>>(route).pipe(
      first(),
    );
  }

}
